<script setup lang="ts">
import { Comment, Fragment, Text, Transition, TransitionGroup, cloneVNode, h, type VNode } from 'vue';

defineOptions({
    name: 'IdeoActionPresence',
    inheritAttrs: false
});

const props = withDefaults(defineProps<{
    group?: boolean;
    animated?: boolean;
    tag?: string;
}>(), {
    group: false,
    animated: true,
    tag: 'div'
});
const slots = useSlots();
const attrs = useAttrs();
const types = new Map<VNode['type'], number>();
const leavingAttributes = new WeakMap<Element, { inert: string | null; hidden: string | null }>();

type PresenceNode = { node: VNode; key: string };

function collect(nodes: VNode[], prefix = ''): PresenceNode[]
{
    return nodes.flatMap((node, index) =>
    {
        if (!node || node.type === Comment || (node.type === Text && !String(node.children || '').trim()))
            return [];
        if (!types.has(node.type))
            types.set(node.type, types.size);
        const key = `${prefix}/${node.key == null ? `index:${index}` : `key:${String(node.key)}`}:type:${types.get(node.type)}`;
        if (node.type === Fragment && Array.isArray(node.children))
            return collect(node.children as VNode[], key);
        if (Array.isArray(node.props?.vnodes) && (node.type as any)?.name === 'Vnodes')
            return collect(node.props.vnodes, key);
        return [{ node, key }];
    });
}

function nodes()
{
    return slots.default?.() || [];
}

function makeInactive(element: Element)
{
    if (!leavingAttributes.has(element))
        leavingAttributes.set(element, { inert: element.getAttribute('inert'), hidden: element.getAttribute('aria-hidden') });
    element.setAttribute('inert', '');
    element.setAttribute('aria-hidden', 'true');
}

function restore(element: Element)
{
    const previous = leavingAttributes.get(element);
    if (!previous)
        return;
    if (previous.inert === null)
        element.removeAttribute('inert');
    else
        element.setAttribute('inert', previous.inert);
    if (previous.hidden === null)
        element.removeAttribute('aria-hidden');
    else
        element.setAttribute('aria-hidden', previous.hidden);
    leavingAttributes.delete(element);
}

const transitionProps = {
    enterActiveClass: '![transition:opacity_0.12s_ease] motion-reduce:!transition-none',
    enterFromClass: '!opacity-0 motion-reduce:!opacity-100',
    enterToClass: '!opacity-100',
    leaveActiveClass: 'pointer-events-none ![transition:opacity_0.12s_ease] motion-reduce:!transition-none',
    leaveFromClass: '!opacity-100',
    leaveToClass: '!opacity-0',
    onBeforeLeave: makeInactive,
    onBeforeEnter: restore,
    onLeaveCancelled: restore
};

function renderItem(item: PresenceNode)
{
    const componentName = (item.node.type as any)?.name || (item.node.type as any)?.__name;
    if (typeof item.node.type === 'string' || ['IdeoAction', 'IdeoActionGroup', 'AiActionButton'].includes(componentName))
        return cloneVNode(item.node, { key: item.key, 'data-action-presence': '' });
    return h('span', { key: item.key, class: 'inline-flex min-w-0', 'data-action-presence': '' }, [item.node]);
}

function PresenceRenderer(renderProps: { nodes: VNode[]; group: boolean; animated: boolean; forwardedAttrs: Record<string, any> })
{
    if (!renderProps.animated)
        return renderProps.group ? h(props.tag, renderProps.forwardedAttrs, renderProps.nodes) : h(Fragment, renderProps.nodes);
    if (!renderProps.group)
        return h(Transition, { ...transitionProps, mode: 'out-in' }, { default: () => renderProps.nodes });
    return h(TransitionGroup, { ...renderProps.forwardedAttrs, ...transitionProps, tag: props.tag }, {
        default: () => collect(renderProps.nodes).map(renderItem)
    });
}
</script>

<template>
    <PresenceRenderer v-bind="{ nodes: nodes(), group: props.group, animated: props.animated, forwardedAttrs: attrs }" />
</template>
